/** Endpoint-only private work records. Hydrate id from the authenticated entity context before parsing. */
import { z } from 'zod';
import { EncryptedArtifactRef } from './artifact-ref.js';
import { BarkRouting } from './bark.js'; // bark-route
import { PrivateEntityId } from './encrypted-entities.js';
import { ModelId, ModelTier } from './model-profiles.js'; // g18
import { TaskPlanTag } from './plan-batch.js'; // g18-batch
import { ProjectSetupState } from './project-setup.js'; // g5-setup
const Id = PrivateEntityId;
const Text = z.string().trim().min(1).max(20_000);
const strings = z.array(z.string().max(20_000)).max(200);
export const PrivateTaskStatus = z.enum([
    'proposed',
    'ready',
    'running',
    'blocked',
    'review',
    'landing',
    'done',
    'cancelled',
]);
export const PrivateTaskRecord = z.strictObject({
    id: Id,
    projectId: Id,
    workstreamId: Id.nullable(),
    title: Text,
    description: z.string().nullable(),
    status: PrivateTaskStatus,
    labels: strings,
    deps: z.array(Id),
    acceptance: strings,
    provenance: z.string(),
    proposed: z.boolean(),
    priority: z.number().int().min(0).max(10),
    agentHint: z.enum(['claude', 'codex']).nullable(),
    requires: strings,
    rationale: z.string().nullable(),
    progress: z.number().nullable(),
    blockedReason: z.string().nullable(),
    runId: z.string().nullable(),
    ideaId: Id.nullable(),
    notBefore: z.number().nullable(),
    parked: z.unknown(),
    landedAt: z.number().nullable(),
    /** Agent progress notes (newest last, bounded by the writer). */
    notes: z
        .array(z.strictObject({ at: z.number(), runId: z.string().max(128), text: z.string().max(4000) }))
        .max(50)
        .optional(),
    /** Lanes: display order within its column (fractional index; router priority stays `priority`). */
    rank: z.number().finite().optional(),
    /** bark-route: the user's routing constraints, kept so a later start (confirm, scheduler) honors them. */
    routing: BarkRouting.optional(),
    /** Lanes: barks this task came from (provenance chip), bounded. */
    barkIds: z.array(Id).max(20).optional(),
    // ---- triage-tools: bark screenshots/files kept with the task (sealed artifact refs) ----
    attachments: z
        .array(z.strictObject({
        encrypted: EncryptedArtifactRef.optional(),
        artifactId: z.string().min(1).max(256),
        name: z.string().max(512).optional(),
        mime: z.string().max(255).optional(),
    }))
        .max(20)
        .optional(),
    // ---- end triage-tools ----
    // ---- g18: the planner's model choice (additive optional fields, like rank/routing above) ----
    tier: ModelTier.optional(),
    model: ModelId.optional(),
    modelReason: z.string().max(200).optional(),
    // ---- end g18 ----
    /** g18-batch: the plan this task was proposed in (`viko_task_create_batch`). */
    plan: TaskPlanTag.optional(),
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const PrivateWorkstreamRecord = z.strictObject({
    id: Id,
    projectId: Id,
    name: Text,
    branch: z.string().nullable(),
    worktree: z.string().nullable(),
    machineId: z.string().nullable(),
    brief: z.string().nullable(),
    status: z.string(),
    ideaId: Id.nullable(),
    liveRun: z
        .object({
        runId: z.string(),
        status: z.string(),
        machineId: z.string(),
        agent: z.enum(['claude', 'codex']).nullable(),
    })
        .nullable(),
    bulkApproved: z.boolean(),
    paused: z.boolean(),
    agent: z.enum(['claude', 'codex']).nullable(),
    counts: z.object({ open: z.number(), proposed: z.number(), done: z.number() }),
    /** Last agent handoff; the brief field carries its text. */
    handoff: z
        .strictObject({
        id: z.string().max(128),
        runId: z.string().max(128),
        reason: z.enum(['context', 'scope_change', 'blocked', 'done', 'other']),
        nextSteps: strings,
        at: z.number(),
    })
        .optional(),
    /** Lanes: lane order (fractional index). */
    rank: z.number().finite().optional(),
    createdBy: z.enum(['user', 'triage', 'planner']).optional(),
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const PrivateRuleRecord = z.strictObject({
    id: Id,
    scope: z.enum(['global', 'project', 'workstream']),
    projectId: Id.nullable(),
    workstreamId: Id.nullable(),
    text: Text,
    provenance: z.string(),
    status: z.enum(['active', 'retired', 'proposed']),
    evidence: z.unknown(),
    lastCitedAt: z.number().nullable(),
    citeCount: z.number(),
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const PrivateIdeaRecord = z.strictObject({
    id: Id,
    text: z.string().max(20_000),
    projectId: Id.nullable(),
    workstreamId: Id.nullable(),
    attachments: z.array(z.unknown()),
    mode: z.enum(['idea', 'direct']),
    status: z.enum(['new', 'archived', 'done']),
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const PrivateProjectRecord = z.strictObject({
    id: Id,
    name: Text,
    status: z.enum(['active', 'archived']),
    config: z.unknown(),
    yaml: z.string(),
    settings: z.object({ brainMachines: z.array(z.string()).optional() }).optional(),
    // ---- account-projects: project entity v2 (all optional; v1 rows still parse) ----
    v: z.literal(2).optional(),
    goal: z.string().max(4000).optional(),
    /** Canonical remote URL; null while the repo is being created. */
    remote: z.string().max(2048).nullable().optional(),
    defaultBranch: z.string().min(1).max(256).optional(),
    /** Desired checkouts; each machine's own table is authoritative for what exists. */
    checkouts: z
        .array(z.strictObject({
        machineId: z.string().min(1).max(128),
        requestedAt: z.number().int().nonnegative(),
        statementId: z.string().min(1).max(256).optional(),
        via: z.enum(['checkout', 'adopt']).optional(),
    }))
        .max(64)
        .optional(),
    // ---- end account-projects ----
    // ---- g5-setup: how the project was set up (links the saved scoping conversation) ----
    setup: ProjectSetupState.optional(),
    // ---- end g5-setup ----
    /** Lanes: pinned tab position (0-based; ⌘1–9 are the first nine), null when unpinned. */
    tab: z.strictObject({ order: z.number().int().min(0).max(999).nullable() }).optional(),
});
export const PrivateTaskPatch = z.strictObject({
    title: Text.optional(),
    description: z.string().nullable().optional(),
    status: PrivateTaskStatus.optional(),
    priority: z.number().int().min(0).max(10).optional(),
    labels: strings.optional(),
    deps: z.array(Id).optional(),
    acceptance: strings.optional(),
});
const FROM_MCP = {
    proposed: 'proposed',
    ready: 'ready',
    in_progress: 'running',
    blocked: 'blocked',
    review: 'review',
    done: 'done',
    dropped: 'cancelled',
};
export const privateTaskStatus = (s) => FROM_MCP[s];
export function toMcpTaskStatus(s) {
    if (s === 'running')
        return 'in_progress';
    if (s === 'cancelled')
        return 'dropped';
    if (s === 'landing')
        return 'review';
    return s;
}
/** Uniform error for plaintext work operations once an account requires encrypted work. */
export const ENCRYPTED_WORK_REQUIRED = 'encrypted_work_required';
/** Legacy plaintext work: browser work-model ops and daemon hub RPCs that carry task/brief text. */
export const isLegacyPlaintextWork = (name) => /^(projects|workstreams|tasks|rules|ideas)\.|^(task|workstream)\.|^handoff$/.test(name);
//# sourceMappingURL=private-work.js.map